/**
 * The competition console — SPEC_5: one page. The header, the worker-dead takeover when the worker
 * has stopped, then two columns: Today (upload, download, what to do next), what the AI did, and the
 * plan's problems and the market data's gaps when there are any; beside them our portfolio and the
 * skills.
 *
 * **Plan problems and Market data are drawn only when something is wrong** (2026-10-22 redesign): a
 * panel that says "all fine" every morning teaches its reader to skip it, and the day it isn't fine
 * is the day it gets skipped.
 *
 * **The worker-dead state overrides the whole page** (Decision 3), as a takeover above Today. Today
 * still renders beneath it: what the day was doing when the worker stopped is still true.
 */

import { Header } from "../components/competition/Header";
import { Today, longDay } from "../components/competition/Today";
import { Pack, troubled } from "../components/competition/Pack";
import { Checks } from "../components/competition/Checks";
import { RunView } from "../components/competition/RunView";
import { Portfolio } from "../components/competition/Portfolio";
import { Skills } from "../components/competition/Skills";
import { Panel } from "../components/ui/Panel";
import { StripAlarm, Takeover } from "../components/ui/States";
import { useCompetition } from "../store/competition";
import { useT } from "../i18n/useT";
import { clock } from "../lib/time";
import { usd } from "../lib/fmt";

export function Console() {
  const { t, lang } = useT();
  const today = useCompetition((s) => s.today);
  const portfolio = useCompetition((s) => s.portfolio);
  const skills = useCompetition((s) => s.skills);
  const log = useCompetition((s) => s.run);

  // `states[0]` is the day's own; the rest ride beside it. worker-dead is lifted out into the
  // takeover, and any other is a line under the header.
  const aside = today.data?.states.slice(1) ?? [];
  const dead = aside.filter((s) => s.name === "worker-dead");
  const lines = aside.filter((s) => s.name !== "worker-dead");
  const run = today.data?.run ?? null;
  const tradeDate = today.data?.trade_date ?? null;

  return (
    <>
      <a className="vh" href="#today">
        {t("app.skip")}
      </a>
      <Header />
      <div aria-live="polite">
        {lines.map((s, i) => (
          <StripAlarm key={`${s.name}-${i}`} state={s} />
        ))}
      </div>
      {dead.map((s, i) => (
        <Takeover key={`${s.name}-${i}`} state={s} kicker={t("banner.workerDead")} />
      ))}

      <main className="console" id="today">
        <div className="console__main">
          <Panel
            name="today"
            h1
            title={tradeDate ? `${t("today.title")} · ${longDay(tradeDate, lang)}` : t("today.title")}
            slot={today}
            retry="today"
            lines={6}
          >
            {(data) => <Today today={data} />}
          </Panel>

          <Panel
            name="run"
            title={t("run.title")}
            info={t("info.run")}
            meta={
              run?.started_at
                ? [
                    `${clock(run.started_at)}${run.completed_at ? ` → ${clock(run.completed_at)}` : ""}`,
                    run.cost_usd ? usd(run.cost_usd) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : null
            }
            slot={today}
            retry="today"
            lines={6}
          >
            {(data) =>
              !data.run ? (
                <p className="empty">{t("run.none")}</p>
              ) : log.id !== data.run.id || log.loading ? (
                <p className="sub">{t("ui.loading")}</p>
              ) : (
                <RunView
                  source={{
                    wakeId: data.run.id,
                    at: data.run.started_at ?? "",
                    occasion: null,
                    preamble: log.preamble,
                    thinking: log.thinking,
                    text: log.text,
                    events: log.events,
                    reply: null,
                    running: data.run.status === "running",
                    endedAt: data.run.completed_at,
                  }}
                />
              )
            }
          </Panel>

          {run?.findings?.length ? (
            <Panel name="checks" title={t("checks.title")} info={t("info.checks")} slot={today} retry="today">
              {(data) => <Checks today={data} />}
            </Panel>
          ) : null}

          {today.data && troubled(today.data) ? (
            <Panel name="pack" title={t("pack.title")} info={t("info.pack")} slot={today} retry="today">
              {(data) => <Pack today={data} />}
            </Panel>
          ) : null}
        </div>

        <aside className="console__aside">
          <Panel name="portfolio" title={t("pf.title")} info={t("info.portfolio")} slot={portfolio} retry="portfolio" lines={10}>
            {(data) => <Portfolio portfolio={data} />}
          </Panel>

          <Panel name="skills" title={t("skills.title")} info={t("info.skills")} slot={skills} retry="skills">
            {(data) => <Skills skills={data} tradeDate={tradeDate} />}
          </Panel>
        </aside>
      </main>
    </>
  );
}
